/**
 * <savings-wizard> — the modal flow behind "Calculate your savings"
 * (snippets/savings-wizard.liquid). Opened by any [data-savings-wizard-open]
 * trigger; that trigger's href stays the no-JS fallback (the old simulator page).
 *
 * The math is a direct port of sections/Savings-simulator.liquid's Monte-Carlo
 * model: lognormal draws from p5/p95 bands →
 *   lbs saved/week = containers-in-use × liters/container × waste% × 2.2
 *   price per lb   = (weekly spend / people) / 12.8
 *   monthly $      = lbs × price × 4   (clamped 0..1000)
 * Result = mean + p5–p95 range (+ mean lbs/month) over DRAWS simulated weeks.
 */
(function () {
  if (customElements.get('savings-wizard')) return;

  const MAX_PEOPLE = 10; // stepper range; model formulas stay honest (pool cap + diminishing household factor)
  const CRUNCH_LINES = [
    'Simulating your weekly shop',
    'Estimating avoidable waste',
    'Weighing the food you keep fresh',
    'Pricing it out per pound',
    'Adding up a month of savings',
  ];
  const SPEND_NOTES = [
    [110, 'Light shopper'],
    [190, 'About the U.S. average'],
    [280, 'Feeding a hungry household'],
    [Infinity, 'Big basket — big savings potential'],
  ];
  const CONFETTI_COLORS = ['#00623d', '#2e9e6b', '#c5e5ab', '#f7ce46', '#ffffff'];

  // v5 model RNG: deterministic per (quiz answers, bundle) — the same answers
  // always show the same numbers. CI convention: [lo, hi] are the 5th/95th
  // percentiles; Normal sd = (hi−lo)/3.29, Lognormal sigma = ln(hi/lo)/3.29.
  const hash32 = (str) => {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  };
  const mulberry32 = (seed) => {
    let s = seed | 0;
    return () => {
      s = (s + 0x6d2b79f5) | 0;
      let x = Math.imul(s ^ (s >>> 15), 1 | s);
      x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
      return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
  };

  // v5 defaults (Appendix A constants table). Admin values override via the
  // data-model attribute; anything unparsable falls back to these.
  const V5_DEFAULTS = {
    wpp: [3.2, 4.2],          // edible waste lbs / person / week (EPA, ReFED)
    eng: [0.30, 0.90],        // fleet engagement share (telemetry, median 58%)
    cpw: [0.25, 1.00],        // lbs / active container / week (telemetry)
    pbp: [11, 15],            // lbs bought / person / week (USDA)
    wsc: { produce: [0.40, 0.50], cooked: [0.30, 0.40], pantry: [0.15, 0.20] },
    cpf: { produce: [1.1, 1.8], cooked: [1.3, 2.2], pantry: [0.9, 1.9] },
    weights: { produce: 50, cooked: 30, pantry: 20 },
  };

  class SavingsWizard extends HTMLElement {
    // Payword choreography knobs (from the v8 simulation): ms per dollar,
    // hold at the zero crossing, row travel time (matches the CSS transition).
    static RATE = 28;
    static PAUSE = 600;
    static SWAP = 750;
    // Email pipeline — the SAME backend + Klaviyo list the lead popup submits
    // to (see SiloPopupManager.submitPopup). One list, one flow, one SSOT.
    static CONTACT_ENDPOINT = 'https://ab-test-node-5f422db672ba.herokuapp.com/api/v1/contact-form';
    static KLAVIYO_LIST_ID = 'UfbrBe';

    connectedCallback() {
      this.state = { containers: 10, people: 2, spend: 150, food: [], step: 0 };
      // Admin calibration knob (theme setting, %): scales the final dollar
      // figures without touching the underlying model.
      this._cal = (Number(this.dataset.calibration) || 100) / 100;
      // v5 model constants — admin-tunable via theme settings (data-model JSON).
      this._draws = Math.max(500, Number(this.dataset.draws) || 10000);
      this._model = V5_DEFAULTS;
      try {
        const m = JSON.parse(this.dataset.model || '{}');
        this._model = {
          wpp: m.wpp || V5_DEFAULTS.wpp,
          eng: m.eng || V5_DEFAULTS.eng,
          cpw: m.cpw || V5_DEFAULTS.cpw,
          pbp: m.pbp || V5_DEFAULTS.pbp,
          wsc: { ...V5_DEFAULTS.wsc, ...(m.wsc || {}) },
          cpf: { ...V5_DEFAULTS.cpf, ...(m.cpf || {}) },
          weights: { ...V5_DEFAULTS.weights, ...(m.weights || {}) },
        };
      } catch (err) { /* defaults hold */ }
      // Admin-controlled step order ("bundle,people,spend,food" in any order).
      // Sanitized: unknown names dropped, duplicates removed, missing appended.
      const DEFAULT_ORDER = ['people', 'spend', 'food'];
      const raw = (this.dataset.stepOrder || '').split(',').map((s) => s.trim().toLowerCase());
      this._order = [...new Set(raw.filter((s) => DEFAULT_ORDER.includes(s)))];
      DEFAULT_ORDER.forEach((s) => { if (!this._order.includes(s)) this._order.push(s); });
      this.stepsEls = Array.from(this.querySelectorAll('[data-sw-step]'));
      this.navEl = this.querySelector('[data-sw-nav]');
      this._step1Hint = this.querySelector('[data-sw-step1-hint]');
      this.backBtn = this.querySelector('[data-sw-back]');
      this.nextBtn = this.querySelector('[data-sw-next]');
      this.progressEl = this.querySelector('[data-sw-progress]');
      this.progressBar = this.querySelector('[data-sw-progress-bar]');
      this.progressCount = this.querySelector('[data-sw-progress-count]');

      // Household visual: one "head" per possible person, lit up to the count.
      const row = this.querySelector('[data-sw-people-row]');
      if (row && !row.children.length) {
        for (let i = 0; i < MAX_PEOPLE; i++) row.appendChild(document.createElement('span'));
      }
      this._syncPeople(false);

      // Food categories are a MULTI-select (v5) with NO default — the visitor
      // must choose; Continue stays disabled until they do.
      this.state.food = [...this.querySelectorAll("[data-sw-set='food'].is-selected")]
        .map((o) => o.dataset.value)
        .filter(Boolean);

      this._onKeydown = (e) => { if (e.key === 'Escape') this.close('escape'); };
      this.addEventListener('click', this);
      this.addEventListener('submit', this);
      this._onSpendBound = this._onSpendBound || ((e) => this._onSpend(e));
      const spendEl = this.querySelector('[data-sw-spend]');
      spendEl?.removeEventListener('input', this._onSpendBound);
      spendEl?.addEventListener('input', this._onSpendBound);

      // Global triggers (the image-CTA button). preventDefault keeps the href
      // as the no-JS fallback to the full simulator page.
      // Pay-over-time module: per-bundle prices from section settings.
      try {
        this._bundlePrices = JSON.parse(this.dataset.bundlePrices || '{}');
      } catch (err) {
        this._bundlePrices = {};
      }

      // Household-size spend pre-selector (admin-tunable via data attr).
      try {
        this._spendPresets = JSON.parse(this.dataset.spendPresets || '{}');
      } catch (err) {
        this._spendPresets = {};
      }
      this._spendTouched = false;
      this._applySpendPreset();

      // Optional unlock gate before the result CTA navigates.
      this._emailGate = this.hasAttribute('data-email-capture');

      // Click triggers: the data attribute, #savings-wizard links, and any
      // admin-listed selectors (Theme settings → Savings wizard popup).
      // DELEGATED at the document level so triggers added after load
      // (apps, dynamic content) work without re-wiring.
      const selectors = ['[data-savings-wizard-open]', "a[href$='#savings-wizard']"]
        .concat((this.dataset.triggerSelectors || '').split(',').map((s) => s.trim()).filter(Boolean));
      this._triggerSelector = selectors.filter((sel) => {
        try { document.querySelector(sel); return true; } catch (err) { return false; } // drop bad admin selectors
      }).join(',');
      // Introspection list (present-at-load triggers) — used by tests/tools.
      const nodes = new Set();
      selectors.forEach((sel) => {
        try { document.querySelectorAll(sel).forEach((n) => nodes.add(n)); } catch (err) { /* skipped above */ }
      });
      this._triggers = [...nodes].filter((n) => !this.contains(n));
      this._onTrigger = (e) => {
        if (!this._triggerSelector) return;
        const hit = e.target.closest(this._triggerSelector);
        if (hit && !this.contains(hit)) {
          e.preventDefault();
          window.siloPopupEvents?.noteTrigger('wizard', 'click', 'selector');
          this.open();
        }
      };
      document.addEventListener('click', this._onTrigger);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onTrigger);
      document.removeEventListener('keydown', this._onKeydown);
      clearInterval(this._crunchTimer);
      cancelAnimationFrame(this._countRaf);
      cancelAnimationFrame(this._rollRaf);
      this._clearFxTimers();
      clearTimeout(this._hideTimer);
      // Theme-editor rerenders can yank the element mid-session — never leave
      // the page scroll-locked behind us.
      if (this.classList.contains('is-open')) document.documentElement.style.overflow = this._prevOverflow || '';
    }

    /* ------------------------------ modal ------------------------------ */

    /* Step telemetry — same popupevents pipeline as the quiz popup. */
    _beacon(eventName, extra) {
      try {
        const sid = (document.cookie.match(/(?:^|; )ab_session_id=([^;]*)/) || [])[1];
        const body = new Blob([JSON.stringify({
          eventName,
          sessionId: sid ? decodeURIComponent(sid) : undefined,
          path: window.location.pathname,
          url: window.location.href,
          popupId: 'savings-wizard',
          timestamp: new Date().toISOString(),
          payload: Object.assign({ ms: this._openedAt ? Date.now() - this._openedAt : 0 }, extra || {}),
        })], { type: 'application/json' });
        navigator.sendBeacon('https://ab-test-node-5f422db672ba.herokuapp.com/api/v1/popup-events', body);
      } catch (e) { /* never break the wizard */ }
    }

    open() {
      clearTimeout(this._hideTimer);
      this.hidden = false;
      this._openedAt = Date.now();
      this._beacon('wizard_opened');
      requestAnimationFrame(() => this.classList.add('is-open'));
      window.siloPopupEvents?.whenShown(this, 'wizard');
      // Lock the DOCUMENT, not <body>: the theme sets overflow-x: clip on <html>, so an overflow on <body> no
      // longer propagates to the viewport — it would make <body> its own (non-scrolling) scroll container,
      // which breaks every position: sticky element on the page and does not even stop the page scrolling.
      this._prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);
      this._celebrated = false;
      this.goTo(0);
      this.querySelector('.sw-close')?.focus({ preventScroll: true });
    }

    // `source` is set only when the VISITOR closed it (button, backdrop,
    // Escape) — that is a dismissal. Every other exit navigates away.
    close(source) {
      this._beacon('wizard_closed', { step: this._currentStep || null });
      if (source) window.siloPopupEvents?.dismissed('wizard', source, this._currentStep || null);
      this.classList.remove('is-open');
      document.documentElement.style.overflow = this._prevOverflow || '';
      document.removeEventListener('keydown', this._onKeydown);
      clearInterval(this._crunchTimer);
      cancelAnimationFrame(this._countRaf);
      cancelAnimationFrame(this._rollRaf);
      this._clearFxTimers();
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => { this.hidden = true; }, 300);
    }

    /* ------------------------------ events ----------------------------- */

    handleEvent(e) {
      if (e.type === 'submit') {
        const form = e.target.closest('[data-sw-gate-form]');
        if (form) { e.preventDefault(); this._gateSubmit(form); }
        return;
      }
      const t = e.target;
      if (t.closest('[data-sw-close]')) return this.close(t.closest('.sw-backdrop') ? 'overlay' : 'close');
      if (t.closest('[data-sw-back]')) return this.goTo(this.state.step - 1);
      if (t.closest('[data-sw-next]')) {
        return this.state.step === this._order.length - 1 ? this.crunch() : this.goTo(this.state.step + 1);
      }
      if (t.closest('[data-sw-again]')) return this.goTo(0);

      const opt = t.closest('[data-sw-set]');
      if (opt) return this._select(opt);

      const stepBtn = t.closest('[data-sw-people]');
      if (stepBtn) return this._people(Number(stepBtn.dataset.swPeople));

      const explainOpen = t.closest('[data-sw-explain-open]');
      if (explainOpen) return this._toggleExplain(true);
      if (t.closest('[data-sw-explain-close]')) return this._toggleExplain(false);

      const rebundle = t.closest('[data-sw-rebundle]');
      if (rebundle) return this._rebundle(Number(rebundle.dataset.swRebundle));

      const mode = t.closest('[data-sw-paymode]');
      if (mode) return this._setPayMode(mode.dataset.swPaymode);

      const apply = t.closest('[data-sw-apply]');
      if (apply) return this._applyOffer();

      const skip = t.closest('[data-sw-email-skip]');
      if (skip) {
        // Same SSOT as the lead popup's decline: popupRefused silences every
        // auto trigger (both popups) for this visitor.
        try { localStorage.setItem('popupRefused', 'true'); } catch (err) { /* ignore */ }
        return this._go();
      }

      const cta = t.closest('[data-sw-cta]');
      if (cta) {
        window.siloPopupEvents?.popupEvent('wizard', 'click', { source: 'cta', step: this._currentStep || undefined });
        this._storeEstimate();
        if (this._shouldGateEmail()) {
          e.preventDefault();
          this._pendingHref = cta.href;
          this._openEmail();
        }
        // no gate → default navigation proceeds with the estimate stored
      }
    }

    // Hand the estimate to the PDP (continuity strip reads this on landing).
    _storeEstimate() {
      try {
        sessionStorage.setItem('siloWizardEstimate', JSON.stringify({
          containers: this.state.containers,
          mean: this._lastMean || 0,
          label: `${this.state.containers}-container bundle`,
        }));
      } catch (err) { /* storage blocked — strip just won't show */ }
    }

    // Gate only when enabled and the visitor doesn't already hold a code.
    _shouldGateEmail() {
      if (!this._emailGate) return false;
      try {
        if (localStorage.getItem('discount_code') || localStorage.getItem('popupFormSubmit') === 'true') return false;
      } catch (err) { /* storage blocked → still show the gate */ }
      return true;
    }

    _openEmail() {
      this._resetLedgerFx();
      // Coupon amount: the LIVE "Popup discount value" setting — same SSOT the
      // grant + toast + PDP preview use. No separate coupon logic.
      this._off = Number(window.ShopifyContext?.popup?.popup_discount_value) || 0;
      this._bundlePrice = Number(this._bundlePrices[String(this.state.containers)]) || 0;

      // "16 containers (4 free)" — free count comes from the pill's data-free
      // (derived in Liquid from the admin bundle label, e.g. "12+4").
      const free = this.querySelector(`[data-sw-rebundle='${this.state.containers}'] [data-free]`)?.dataset.free;
      this._bundleLine = `${this.state.containers} containers${free ? ` (${free} free)` : ''}`;

      // Monthly mode needs Affirm + a price; hide the toggle (and pin "once")
      // when either is missing. The ledger itself needs a price at all.
      const bl = this.querySelector('[data-sw-bl]');
      const hasPrice = this._bundlePrice > 0;
      if (bl) bl.hidden = !hasPrice;
      this._toggleAllowed = hasPrice && this.hasAttribute('data-affirm');
      const toggle = this.querySelector('[data-sw-paytoggle]');
      if (toggle) toggle.hidden = !this._toggleAllowed;
      // Default view: MONTHLY (when the Affirm toggle is available) — the
      // visitor's own toggle choice persists across reopens.
      const defMode = this.dataset.defaultPaymode === 'monthly' && this._toggleAllowed ? 'monthly' : 'once';
      if (hasPrice) this._setPayMode(this._payMode || defMode, true);

      this._show('email');
      this.navEl.hidden = true;
      this.progressEl.style.display = 'none';

      // Skip-apply mode (admin setting): land with the coupon already applied —
      // discounted ledger + the email gate open. Same end-state as _applyOffer,
      // just without the button and the choreography.
      if (this.hasAttribute('data-skip-apply') && !this._applied) {
        this._beacon('wizard_apply', { auto: true });
        this._storeEstimate();
        this._applied = true;
        this._renderLedger(true);
        this._showGate();
        return;
      }

      this.querySelector('[data-sw-apply]')?.focus({ preventScroll: true });
    }

    // Toggle handler: re-renders the ledger for the chosen mode. Works before
    // AND after the coupon is applied (post-apply it shows the discounted
    // numbers); locked only while the apply choreography is mid-flight.
    _setPayMode(mode, initial = false) {
      if (this._animating) return;
      if (!initial && mode !== this._payMode) this._beacon('wizard_paymode', { mode });
      this._payMode = mode;
      this.querySelectorAll('[data-sw-paymode]').forEach((b) => {
        const on = b.dataset.swPaymode === mode;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      this._renderLedger(initial);
    }

    // THE ledger renderer — the one place that lays out rows, word, receipt
    // and caption for the current {mode, applied} state. Idempotent, so it
    // also normalizes whatever the choreography moved. When the savings beat
    // the price (net < 0) the ledger starts savings-first in "Save" mode.
    _renderLedger(initial = false) {
      const P = this._bundlePrice;
      const M = this._lastMean || 0;
      const off = this._applied ? this._off || 0 : 0;
      const Peff = Math.max(0, P - off);
      const monthly = this._payMode === 'monthly';
      const pay = monthly ? Math.ceil(Peff / 12) : Peff;
      const save = monthly ? M : M * 12;
      const net = pay - save;
      const flipped = net < 0;
      this._flipped = flipped;

      const q = (sel) => this.querySelector(sel);
      const l1 = q('[data-bl-l1]');
      const l2 = q('[data-bl-l2]');
      const num1 = q('[data-bl-num1]');
      const num2 = q('[data-bl-num2]');
      const payEl = q('[data-bl-pay]');
      const saveEl = q('[data-bl-save]');
      if (!l1 || !l2 || !num1 || !num2 || !payEl || !saveEl) return;

      const priceLbl = monthly ? 'Pay over time' : 'Your Silo, paid once';
      const priceSub = monthly ? 'as low as, with affirm' : '';
      const saveLbl = monthly ? 'Your kitchen saves' : 'Year-one savings';
      const saveSub = monthly ? '' : `$${M} × 12 months`;
      const setSub = (el, text) => { if (el) { el.textContent = text; el.hidden = !text; } };
      const l1sub = q('[data-bl-l1sub]');
      const l2sub = q('[data-bl-l2sub]');

      // Split mode (admin setting): the price breaks into "Silo device"
      // (which carries the discount) + "[n] containers set". Fixed row order,
      // no savings-first flip — the totals math is unchanged.
      // Split applies to Pay once only — Pay monthly keeps the merged ledger.
      const devP = Number(this.dataset.devicePrice) || 0;
      const split = this.hasAttribute('data-split-price') && devP > 0 && P > devP && !monthly;
      const row0 = q("[data-bl-row='0']");
      if (row0) row0.hidden = !split;
      if (split) {
        const l0 = q('[data-bl-l0]');
        const l0sub = q('[data-bl-l0sub]');
        const devEl = q('[data-bl-dev]');
        const devEff = Math.max(0, devP - off);
        const contP = P - devP;
        const containers = Number(this.state.containers) || 0;
        const free = Number(this.querySelector(`[data-sw-rebundle='${containers}'] [data-free]`)?.dataset.free) || 0;
        const paid = Math.max(0, containers - free);

        if (l0) l0.textContent = this.dataset.deviceLabel || 'Silo device';
        setSub(l0sub, this._applied && off > 0 ? `Was $${devP} • Your $${off} off` : '');
        if (l0sub && this._applied && off > 0) {
          l0sub.innerHTML = `Was <s>$${devP}</s> • Your $${off} off`;
        }
        if (devEl) devEl.textContent = `$${monthly ? Math.ceil(devEff / 12) : devEff}`;

        l1.textContent = `${containers} containers set`;
        setSub(l1sub, free > 0 ? `${paid} + ${free} free containers` : `${containers} containers`);
        l2.textContent = saveLbl;
        setSub(l2sub, saveSub);
        num1.classList.remove('sw-bl3__num--save');
        this._setNum(payEl, monthly ? Math.ceil(contP / 12) : contP);
        num2.classList.add('sw-bl3__num--save');
        this._setNum(saveEl, save, true);
        this._flipped = false;

        const feelsS = q('[data-bl-feels]');
        if (feelsS) feelsS.textContent = `$${Math.abs(net)}`;
        const wordS = q('[data-bl-word]');
        if (wordS) {
          wordS.textContent = net < 0 ? 'Saving' : 'Paying';
          wordS.classList.remove('blink');
          wordS.classList.toggle('saving', net < 0);
        }
        const sufS = (sel, show) => { const el = q(sel); if (el) el.hidden = !show; };
        sufS('[data-bl-devsuf]', monthly);
        sufS('[data-bl-paysuf]', monthly);
        sufS('[data-bl-savesuf]', monthly);
        sufS('[data-bl-feelssuf]', monthly);
        const tagS = q('[data-bl-code]');
        if (tagS) { tagS.hidden = true; tagS.classList.remove('show'); }
        const cap1S = q('[data-bl-caption]');
        if (cap1S) {
          if (monthly) {
            cap1S.textContent = '12 monthly payments with Affirm · the savings keep going after month 12';
          } else if (M > 0 && Peff > 0) {
            const payback = Math.max(1, Math.ceil(Peff / M));
            cap1S.innerHTML = `Pays for itself in <b>~${payback} month${payback === 1 ? '' : 's'}</b>, then all savings are yours`;
          } else {
            cap1S.textContent = `${this._bundleLine} · year two, it's all yours`;
          }
          cap1S.classList.remove('is-hidden');
        }
        const cap2S = q('[data-bl-caption2]');
        if (cap2S) { cap2S.classList.add('is-hidden'); cap2S.setAttribute('aria-hidden', 'true'); }
        const feelsWrapS = q('[data-bl-feels-wrap]');
        if (!initial && feelsWrapS) {
          feelsWrapS.classList.remove('pop');
          void feelsWrapS.offsetWidth;
          feelsWrapS.classList.add('pop');
        }
        return;
      }

      if (flipped) {
        // Savings on top (green, positive); the price below (ink, negative).
        l1.textContent = saveLbl;
        setSub(l1sub, saveSub);
        l2.textContent = priceLbl;
        setSub(l2sub, priceSub);
        num1.classList.add('sw-bl3__num--save');
        this._setNum(payEl, save);
        num2.classList.remove('sw-bl3__num--save');
        this._setNum(saveEl, pay, true);
      } else {
        l1.textContent = priceLbl;
        setSub(l1sub, priceSub);
        l2.textContent = saveLbl;
        setSub(l2sub, saveSub);
        num1.classList.remove('sw-bl3__num--save');
        this._setNum(payEl, pay);
        num2.classList.add('sw-bl3__num--save');
        this._setNum(saveEl, save, true);
      }

      const feels = q('[data-bl-feels]');
      if (feels) feels.textContent = `$${Math.abs(net)}`;
      const word = q('[data-bl-word]');
      if (word) {
        word.textContent = flipped ? 'Saving' : 'Paying';
        word.classList.remove('blink');
        word.classList.toggle('saving', flipped);
      }

      const suf = (sel, show) => { const el = q(sel); if (el) el.hidden = !show; };
      suf('[data-bl-paysuf]', monthly);
      suf('[data-bl-savesuf]', monthly);
      suf('[data-bl-feelssuf]', monthly);

      // Receipt rides with the price row; visible only once applied.
      // New-design mode (split setting on): monthly shows no receipt — the
      // "as low as, with affirm" sub carries the context (mock parity).
      const newDesign = this.hasAttribute('data-split-price');
      const tag = q('[data-bl-code]');
      if (tag) {
        const priceRow = flipped ? "[data-bl-row='2']" : "[data-bl-row='1']";
        q(`${priceRow} .sw-bl3__labels`)?.appendChild(tag);
        if (this._applied && off > 0 && !(newDesign && monthly)) {
          tag.textContent = `✓ $${P} − your $${off} OFF`;
          tag.hidden = false;
          tag.classList.add('show');
        } else {
          tag.hidden = true;
          tag.classList.remove('show');
        }
      }

      // Caption: the savings-first post-apply state earns the payback line.
      const cap1 = q('[data-bl-caption]');
      const cap2 = q('[data-bl-caption2]');
      if (cap1) {
        if (monthly) {
          cap1.textContent = '12 monthly payments with Affirm · the savings keep going after month 12';
        } else if (M > 0 && Peff > 0) {
          // Same model math as the post-apply payback line: price ÷ monthly savings.
          const payback = Math.max(1, Math.ceil(Peff / M));
          cap1.innerHTML = `Pays for itself in <b>~${payback} month${payback === 1 ? '' : 's'}</b>, then all savings are yours`;
        } else {
          cap1.textContent = `${this._bundleLine} · year two, it's all yours`;
        }
      }
      const showCap2 = !!(this._applied && flipped && M > 0 && Peff > 0) && !(newDesign && monthly);
      if (cap2) {
        if (showCap2) {
          const months = Math.max(1, Math.ceil(Peff / M));
          cap2.innerHTML = `Covers itself in <b>~${months} month${months === 1 ? '' : 's'}</b> — then it's all yours.`;
        }
        cap2.classList.toggle('is-hidden', !showCap2);
        cap2.setAttribute('aria-hidden', showCap2 ? 'false' : 'true');
      }
      cap1?.classList.toggle('is-hidden', showCap2);

      const feelsWrap = q('[data-bl-feels-wrap]');
      if (!initial && feelsWrap) {
        feelsWrap.classList.remove('pop');
        void feelsWrap.offsetWidth;
        feelsWrap.classList.add('pop');
      }
    }

    // "Apply my $X OFF": play the payword choreography — the receipt fades
    // in, the price and net numbers roll live, and when the savings overtake
    // the price the word flips Pay → Save while the rows trade places. If the
    // ledger already started savings-first, the price simply sheds the coupon
    // while the Save number climbs. Ends on the email gate (which is the only
    // place the coupon is actually granted).
    _applyOffer() {
      if (this._applied || this._animating) return;
      // Split-price ledger: apply lands instantly (the discount moves the
      // device row; the payword choreography belongs to the merged row).
      if (this.hasAttribute('data-split-price') && Number(this.dataset.devicePrice) > 0) {
        this._beacon('wizard_apply');
        this._storeEstimate();
        this._applied = true;
        this._renderLedger(true);
        return this._showGate();
      }
      this._beacon('wizard_apply');
      this.querySelector('[data-sw-apply]')?.setAttribute('aria-disabled', 'true');
      this._storeEstimate();

      const off = this._off;
      const P = this._bundlePrice;
      if (!(off > 0) || !(P > 0)) { this._applied = true; return this._showGate(); }
      this._animating = true;

      // Toggle locked only while the numbers move; it comes back after,
      // re-rendering the discounted ledger in either mode.
      const toggle = this.querySelector('[data-sw-paytoggle]');
      if (toggle) toggle.hidden = true;

      const monthly = this._payMode === 'monthly';
      const M = this._lastMean || 0;
      const pay = monthly ? Math.ceil(P / 12) : P; // as displayed pre-apply
      const save = monthly ? M : M * 12;
      const payTo = monthly ? Math.max(0, Math.ceil((P - off) / 12)) : Math.max(0, P - off);
      const payEl = this.querySelector('[data-bl-pay]');
      const saveEl = this.querySelector('[data-bl-save]');
      const netEl = this.querySelector('[data-bl-feels]');
      if (!payEl || !saveEl || !netEl) {
        this._applied = true;
        this._animating = false;
        return this._showGate();
      }

      // Receipt under the price row's label: "✓ $696 − your $100 OFF".
      const tag = this.querySelector('[data-bl-code]');
      if (tag) {
        tag.textContent = `✓ $${P} − your $${off} OFF`;
        tag.hidden = false;
        requestAnimationFrame(() => tag.classList.add('show'));
      }

      // Every path lands here: commit the applied state, normalize the ledger
      // through the single renderer (receipt, caption, word — all consistent),
      // unlock the toggle, reveal the gate.
      const finish = () => this._fxTimeout(() => {
        this._applied = true;
        this._animating = false;
        this._renderLedger(true);
        if (toggle) toggle.hidden = !this._toggleAllowed;
        this._showGate();
      }, 500);

      if (this._flipped) {
        // Already savings-first: the price (bottom, negative) sheds the coupon
        // while the Save number climbs in step.
        this._roll(
          [{ el: saveEl, from: pay, to: payTo, neg: true }, { el: netEl, from: save - pay, to: save - payTo }],
          Math.max(1, pay - payTo) * SavingsWizard.RATE,
          finish,
        );
        return;
      }

      const netTo = payTo - save;
      if (netTo >= 0) {
        // Normal: both numbers descend together.
        this._roll(
          [{ el: payEl, from: pay, to: payTo }, { el: netEl, from: pay - save, to: netTo }],
          Math.max(1, pay - payTo) * SavingsWizard.RATE,
          finish,
        );
        return;
      }

      // FLIP — the discount pushes the savings past the price. Phase A: the
      // price descends to meet the fixed savings while the net runs to zero.
      const cross = Math.min(pay, save);
      this._roll(
        [{ el: payEl, from: pay, to: cross }, { el: netEl, from: Math.max(0, pay - save), to: 0 }],
        Math.max(1, pay - cross) * SavingsWizard.RATE,
        () => this._fxTimeout(() => this._flipRows(cross, payTo, save, finish), SavingsWizard.PAUSE),
      );
    }

    // Pay $0 and Save $0 are the same fact, so the word trades during the
    // stillness — then the rows visibly trade places (colors and minus ride
    // along) before the discount finishes on the second line.
    _flipRows(cross, payTo, save, done) {
      this._setWord('Saving');
      const row1 = this.querySelector("[data-bl-row='1']");
      const row2 = this.querySelector("[data-bl-row='2']");
      const num1 = this.querySelector('[data-bl-num1]');
      const num2 = this.querySelector('[data-bl-num2]');
      const payEl = this.querySelector('[data-bl-pay]');
      const saveEl = this.querySelector('[data-bl-save]');
      const netEl = this.querySelector('[data-bl-feels]');
      if (!row1 || !row2 || !num1 || !num2 || !payEl || !saveEl || !netEl) return done();

      // Pre-swap trade: top reads as the (negative, ink) price, bottom as the
      // (positive, green) savings — so the slide carries the right colors.
      num1.classList.remove('sw-bl3__num--save');
      this._setNum(payEl, cross, true);
      num2.classList.add('sw-bl3__num--save');
      this._setNum(saveEl, cross);

      const commit = () => {
        [row1, row2].forEach((r) => {
          r.style.transition = 'none';
          r.style.transform = '';
        });
        // Top row is now the savings; bottom row the discounted price.
        const l1 = this.querySelector('[data-bl-l1]');
        const l2 = this.querySelector('[data-bl-l2]');
        const l1sub = this.querySelector('[data-bl-l1sub]');
        const l2sub = this.querySelector('[data-bl-l2sub]');
        if (l1 && l2) {
          const priceLbl = l1.textContent;
          l1.textContent = l2.textContent;
          l2.textContent = priceLbl;
        }
        if (l1sub && l2sub) {
          l1sub.textContent = l2sub.textContent;
          l1sub.hidden = !l1sub.textContent;
          l2sub.textContent = '';
          l2sub.hidden = true;
        }
        const tag = this.querySelector('[data-bl-code]');
        const labels2 = row2.querySelector('.sw-bl3__labels');
        if (tag && labels2) labels2.appendChild(tag); // receipt follows the price
        num1.classList.add('sw-bl3__num--save');
        this._setNum(payEl, cross);
        num2.classList.remove('sw-bl3__num--save');
        this._setNum(saveEl, cross, true);
        void row1.offsetHeight;
        [row1, row2].forEach((r) => { r.style.transition = ''; });

        // Phase B: the discount finishes on the second line; the number
        // climbs as "Save". (When the savings already exceeded the full
        // price, the top number climbs to the true savings in the same
        // breath.)
        const jobs = [
          { el: saveEl, from: cross, to: payTo, neg: true },
          { el: netEl, from: 0, to: save - payTo },
        ];
        if (save !== cross) jobs.push({ el: payEl, from: cross, to: save });
        this._fxTimeout(() => {
          this._roll(jobs, Math.max(1, cross - payTo) * SavingsWizard.RATE, done);
        }, 300);
      };

      if (this._reducedMotion()) { commit(); return; }
      const d = row2.offsetTop - row1.offsetTop;
      row1.style.transform = `translateY(${d}px)`;
      row2.style.transform = `translateY(${-d}px)`;
      this._fxTimeout(commit, SavingsWizard.SWAP);
    }

    // Ledger number writer: the minus hangs in the gutter (absolute, outside
    // the digit box) so the $ amounts of both rows stay optically aligned.
    _setNum(el, value, neg = false) {
      el.innerHTML = `${neg ? "<i class='sw-bl3__sign'>−</i>" : ''}$${value}`;
    }

    // rAF number roll shared by every phase (eased, tabular-nums keep it steady).
    _roll(jobs, dur, done) {
      if (this._reducedMotion()) {
        jobs.forEach((j) => this._setNum(j.el, j.to, j.neg));
        if (done) done();
        return;
      }
      let start = null;
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2);
      const step = (ts) => {
        if (start === null) start = ts;
        const p = Math.min((ts - start) / dur, 1);
        const e = ease(p);
        jobs.forEach((j) => this._setNum(j.el, Math.round(j.from + (j.to - j.from) * e), j.neg));
        if (p < 1) this._rollRaf = requestAnimationFrame(step);
        else if (done) done();
      };
      this._rollRaf = requestAnimationFrame(step);
    }

    // The Pay/Save word blinks out, trades, blinks back (green when saving).
    _setWord(word) {
      const el = this.querySelector('[data-bl-word]');
      if (!el) return;
      const commit = () => {
        el.textContent = word;
        el.classList.remove('blink');
        el.classList.toggle('saving', word === 'Saving');
      };
      if (this._reducedMotion()) return commit();
      el.classList.add('blink');
      this._fxTimeout(commit, 250);
    }

    // Apply card ⇄ email gate cross-fade ("Where should we send your code?").
    _showGate() {
      const applyPanel = this.querySelector('[data-sw-panel-apply]');
      const gatePanel = this.querySelector('[data-sw-panel-gate]');
      if (!gatePanel) return this._go(); // markup missing → just continue
      applyPanel?.classList.add('sw-panel--hidden');
      gatePanel.classList.remove('sw-panel--hidden');
      if (window.matchMedia?.('(pointer: fine)').matches) {
        // Focus after the cross-fade: the panel transitions visibility, and
        // focus() on a not-yet-visible element is silently dropped.
        this._fxTimeout(() => gatePanel.querySelector('input')?.focus({ preventScroll: true }), 400);
      }
    }

    // Gate submit: validate, THEN grant the coupon (the discount is earned by
    // the email — same SSOT keys the lead popup uses), capture the address,
    // continue to the chosen bundle. Skipping the gate skips the discount.
    _gateSubmit(form) {
      if (this._gateBusy) return;
      const input = form.querySelector("input[type='email']");
      const err = this.querySelector('[data-sw-email-error]');
      const email = (input?.value || '').trim();
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      input?.classList.toggle('is-invalid', !ok);
      if (err) err.hidden = ok;
      if (!ok) return input?.focus({ preventScroll: true });
      this._gateBusy = true;
      const submitBtn = form.querySelector("button[type='submit']");
      submitBtn?.setAttribute('aria-disabled', 'true');
      submitBtn?.classList.add('is-loading');
      submitBtn?.setAttribute('aria-busy', 'true');
      const code = window.ShopifyContext?.popup?.popup_discount_code;
      if (code) {
        try {
          localStorage.setItem('discount_code', code);
          localStorage.setItem('popupFormSubmit', 'true');
          document.cookie = `discount_code=${code};path=/;max-age=${60 * 60 * 24 * 7}`;
        } catch (storageErr) { /* storage blocked — cookie attempt still made */ }
      }
      // Dispatch the Klaviyo capture (keepalive — survives the navigation),
      // then continue. The race caps tracking lookups so the handoff never
      // stalls on them.
      this._captureEmail(email).finally(() => this._go());
    }

    async _captureEmail(email) {
      try {
        const capped = (promise, fallback) =>
          Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(fallback), 800))])
            .catch(() => fallback);
        const mgr = window.siloPopupManager;
        const tracking = mgr?.getTrackingContext ? await capped(mgr.getTrackingContext(), {}) : {};
        const campaign = mgr?.getCampaignData ? await capped(mgr.getCampaignData(), null) : null;

        // EXACT mirror of SiloPopupManager.submitPopup's payload — same field
        // names, same shapes, same list; only the form_id names the wizard.
        const payload = {
          form_id: `silo-savings-wizard ${window.location.pathname}`,
          submitted_data: {
            'contact[email]': email,
          },
          choices: {
            step1: String(this.state.people),
            step2: String(this.state.spend),
            step3: (this.state.food || []).join(','),
            // Full answer set for future stats — `choices` is Mixed end-to-end.
            people: this.state.people,
            weekly_spend: this.state.spend,
            foods: this.state.food || [],
            bundle_recommended: this._recommended ?? null,
            bundle_selected: this.state.containers ?? null,
            bundle_price: this._bundlePrice ?? null,
            bundle_line: this._bundleLine ?? null,
            pay_mode: this._payMode ?? null,
            estimate: this._lastResult ? {
              median: Math.round(this._lastResult.median),
              mean: Math.round(this._lastResult.mean),
              p05: Math.round(this._lastResult.p05),
              p95: Math.round(this._lastResult.p95),
              lbs_month: Math.round(this._lastResult.lbsMonth),
            } : null,
          },
          form_type: 'popup',
          sessionId: tracking.sessionId,
          utms: tracking.utms,
          campaign,
          userId: tracking.userId,
          deviceId: tracking.deviceId,
          listId: SavingsWizard.KLAVIYO_LIST_ID,
        };
        // AWAIT the send (capped) before the caller navigates — Safari/iOS
        // don't reliably honor keepalive, and an immediate navigation kills
        // the in-flight request there. The gate button's spinner covers the
        // wait; keepalive remains as the safety net for the tail.
        this._beacon('wizard_submit');
        window.siloPopupEvents?.submitted('wizard', payload.form_id, this._currentStep || null);
        await Promise.race([
          fetch(SavingsWizard.CONTACT_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
          }).catch(() => { /* best-effort */ }),
          new Promise((resolve) => setTimeout(resolve, 2500)),
        ]);
      } catch (err) { /* best-effort — never block the handoff */ }
    }

    _go() {
      this._storeEstimate();
      const href = this._pendingHref || '/products/silo-vacuum-storage-system';
      window.location.assign((window.siloV2Href ? window.siloV2Href(href) : href));
    }

    // Fresh choreography state every time the step opens (reopen-safe).
    _resetLedgerFx() {
      this._applied = false;
      this._animating = false;
      this._gateBusy = false;
      cancelAnimationFrame(this._rollRaf);
      this._clearFxTimers();
      const word = this.querySelector('[data-bl-word]');
      if (word) { word.textContent = 'Paying'; word.classList.remove('blink', 'saving'); }
      const tag = this.querySelector('[data-bl-code]');
      if (tag) {
        tag.hidden = true;
        tag.classList.remove('show');
        this.querySelector("[data-bl-row='1'] .sw-bl3__labels")?.appendChild(tag);
      }
      this.querySelectorAll('.sw-bl3__row--slide').forEach((r) => { r.style.transform = ''; });
      this.querySelector('[data-bl-num1]')?.classList.remove('sw-bl3__num--save');
      this.querySelector('[data-bl-num2]')?.classList.add('sw-bl3__num--save');
      this.querySelector('[data-bl-caption]')?.classList.remove('is-hidden');
      const cap2 = this.querySelector('[data-bl-caption2]');
      if (cap2) { cap2.classList.add('is-hidden'); cap2.setAttribute('aria-hidden', 'true'); }
      this.querySelector('[data-sw-panel-apply]')?.classList.remove('sw-panel--hidden');
      this.querySelector('[data-sw-panel-gate]')?.classList.add('sw-panel--hidden');
      this.querySelector('[data-sw-apply]')?.removeAttribute('aria-disabled');
      const gateBtn = this.querySelector('[data-sw-gate-form] button');
      if (gateBtn) {
        gateBtn.classList.remove('is-loading');
        gateBtn.removeAttribute('aria-disabled');
        gateBtn.removeAttribute('aria-busy');
      }
      const l2sub = this.querySelector('[data-bl-l2sub]');
      if (l2sub) l2sub.hidden = false;
    }

    _reducedMotion() {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    }

    // Tracked timeouts for the apply choreography: closing or reopening the
    // step clears them all, so a stale phase can never fire into a fresh run.
    _fxTimeout(fn, ms) {
      this._fxTimers = this._fxTimers || [];
      const id = setTimeout(fn, ms);
      this._fxTimers.push(id);
      return id;
    }

    _clearFxTimers() {
      (this._fxTimers || []).forEach(clearTimeout);
      this._fxTimers = [];
    }


    _select(opt) {
      const key = opt.dataset.swSet;
      if (key === 'food') {
        // v5: multi-select, no default — toggling freely; the Continue button
        // gates the step until at least one category is chosen.
        const set = new Set(this.state.food || []);
        const val = opt.dataset.value;
        if (set.has(val)) set.delete(val); else set.add(val);
        this.state.food = [...set];
        opt.parentElement.querySelectorAll("[data-sw-set='food']").forEach((o) => {
          const on = this.state.food.includes(o.dataset.value);
          o.classList.toggle('is-selected', on);
          o.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        this._syncFoodUI();
        return;
      }
      opt.parentElement.querySelectorAll('[data-sw-set]').forEach((o) => {
        const on = o === opt;
        o.classList.toggle('is-selected', on);
        o.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      this.state[key] = Number(opt.dataset.value);
      // No auto-advance: the user confirms with Continue.
    }

    _people(delta) {
      this.state.people = Math.min(MAX_PEOPLE, Math.max(1, this.state.people + delta));
      this._syncPeople(true);
      this._applySpendPreset();
    }

    // Household-size spend pre-selector: sets the slider's starting point
    // (1→$150, 2→$180, 3→$220, 4+→$250 by default) until the visitor moves
    // the slider themselves — their own number always wins after that.
    _applySpendPreset() {
      if (this._spendTouched) return;
      const key = String(Math.min(4, this.state.people));
      const preset = Number(this._spendPresets[key]);
      if (!(preset > 0)) return;
      const slider = this.querySelector('[data-sw-spend]');
      if (!slider) return;
      this.state.spend = preset;
      slider.value = preset;
      const valueEl = this.querySelector('[data-sw-spend-value]');
      if (valueEl) valueEl.textContent = preset;
      const pct = ((preset - slider.min) / (slider.max - slider.min)) * 100;
      slider.style.setProperty('--sw-fill', `${pct}%`);
      const note = SPEND_NOTES.find(([cap]) => preset < cap);
      const noteEl = this.querySelector('[data-sw-spend-note]');
      if (noteEl) noteEl.textContent = note ? note[1] : '';
    }

    // The bundle is recommended, not asked. Matrix-driven (admin-managed):
    // household size row -> "threshold:bundle" pairs + final bundle, matched
    // against the WEEKLY spend answer. E.g. "130:4,230:10,16" = under $130 -> 4,
    // under $230 -> 10, otherwise 16. Households above 4 use the 4+ row.
    // "12 + 4 free containers" — the same wording the bottom line uses; free count
    // comes from the pill the merchant configured (wizard_pill_N = "12+4").
    _bundleLabel(n) {
      const total = Number(n) || 0;
      const free = Number(this.querySelector(`[data-sw-rebundle='${total}'] [data-free]`)?.dataset.free) || 0;
      return free > 0 ? `${total - free} + ${free} free containers` : `${total} containers`;
    }

    _recommendBundle() {
      const HOU = Math.max(1, Math.min(10, this.state.people));
      const spend = Number(this.state.spend) || 0;
      let rules = null;
      try {
        const table = JSON.parse(this.dataset.recommendRules || '{}');
        rules = table[String(Math.min(HOU, 4))] || null;
      } catch (e) { rules = null; }
      if (rules) {
        const parts = String(rules).split(',').map((x) => x.trim()).filter(Boolean);
        for (const part of parts) {
          const [t, b] = part.split(':');
          if (b === undefined) {
            const last = parseInt(t, 10); // final "otherwise" bundle
            if ([4, 10, 16, 24].includes(last)) return last;
            break;
          }
          const threshold = Number(t);
          const bundle = parseInt(b, 10);
          if (Number.isFinite(threshold) && spend < threshold && [4, 10, 16, 24].includes(bundle)) {
            return bundle;
          }
        }
      }
      // Fallback (unparseable rules): the previous household-size heuristic.
      const cooked = (this.state.food || []).includes('cooked');
      if (HOU >= 6 || (cooked && HOU >= 4)) return 24;
      if (HOU >= 4) return 16;
      return 10;
    }

    _syncPeople(pop) {
      const valueEl = this.querySelector('[data-sw-people-value]');
      const unitEl = this.querySelector('[data-sw-people-unit]');
      if (!valueEl) return;
      valueEl.textContent = this.state.people;
      unitEl.textContent = this.state.people === 1 ? 'person' : 'people';
      if (pop) {
        valueEl.classList.remove('pop');
        void valueEl.offsetWidth;
        valueEl.classList.add('pop');
      }
      this.querySelectorAll('[data-sw-people-row] span').forEach((dot, i) => {
        dot.classList.toggle('on', i < this.state.people);
      });
    }

    _onSpend(e) {
      this._spendTouched = true;
      this.state.spend = Number(e.target.value);
      this.querySelector('[data-sw-spend-value]').textContent = this.state.spend;
      const pct = ((this.state.spend - e.target.min) / (e.target.max - e.target.min)) * 100;
      e.target.style.setProperty('--sw-fill', `${pct}%`);
      const note = SPEND_NOTES.find(([cap]) => this.state.spend < cap);
      this.querySelector('[data-sw-spend-note]').textContent = note ? note[1] : '';
    }

    // Food step UI: the LAST chosen category's paragraph cross-fades in
    // (selection order is preserved — Set keeps insertion order), and
    // Continue stays disabled until the visitor has picked at least one.
    _syncFoodUI() {
      const sel = this.state.food || [];
      const last = sel[sel.length - 1];
      this.querySelectorAll('[data-food-desc]').forEach((p) => {
        const on = p.dataset.foodDesc === last;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      if (this._order[this.state.step] === 'food' && this.nextBtn) {
        this.nextBtn.disabled = sel.length === 0;
      }
    }

    /* ------------------------------ steps ------------------------------ */

    goTo(step) {
      this._crunching = false;
      this.state.step = Math.max(0, Math.min(step, this._order.length - 1));
      this._show(this._order[this.state.step]);
      this.navEl.hidden = false;
      this.progressEl.style.display = '';
      this.backBtn.hidden = this.state.step === 0;
      if (this._step1Hint) this._step1Hint.hidden = this.state.step !== 0;
      const last = this._order.length - 1;
      this.nextBtn.innerHTML = this.state.step === last
        ? 'See my savings <span aria-hidden="true">&rarr;</span>'
        : 'Continue <span aria-hidden="true">&rarr;</span>';
      this.nextBtn.disabled = false;
      this._syncFoodUI();
      this.progressBar.style.width = `${((this.state.step + 1) / this._order.length) * 100}%`;
      if (this.progressCount) this.progressCount.textContent = `${this.state.step + 1}/${this._order.length}`;
    }

    _show(name) {
      if (name !== this._currentStep) {
        this._currentStep = name;
        this._beacon('wizard_step', { step: name });
      }
      this.stepsEls.forEach((el) => {
        const on = el.dataset.swStep === name;
        el.hidden = !on;
        if (on) {
          // retrigger the entry animation
          el.style.animation = 'none';
          void el.offsetWidth;
          el.style.animation = '';
        }
      });
    }

    /* --------------------------- crunch + math -------------------------- */

    crunch() {
      if (this._crunching) return; // a second click mid-crunch double-runs the sim
      this._crunching = true;
      // Lock in the recommendation for this run (re-runs via the bundle cards
      // keep the badge on the recommended one while selection moves).
      this._recommended = this._recommendBundle();
      this.state.containers = this._recommended;
      this._show('crunch');
      this.navEl.hidden = true;
      this.progressBar.style.width = '100%';
      if (this.progressCount) this.progressCount.textContent = '✓';

      const lineEl = this.querySelector('[data-sw-crunch-line]');
      let i = 0;
      lineEl.textContent = CRUNCH_LINES[0];
      clearInterval(this._crunchTimer);
      this._crunchTimer = setInterval(() => {
        i += 1;
        if (i < CRUNCH_LINES.length) lineEl.textContent = CRUNCH_LINES[i];
      }, 550);

      // One deterministic run per bundle: powers the result and instant
      // bundle switching from the SAME simulation output.
      this._allStats = {};
      [4, 10, 16, 24].forEach((b) => { this._allStats[b] = this._simulate(b); });
      setTimeout(() => {
        clearInterval(this._crunchTimer);
        this._crunching = false;
        this._freshResult = true;
        this._showResult(this._allStats[this.state.containers]);
      }, CRUNCH_LINES.length * 550 + 250);
    }

    // v5 Monte-Carlo: each draw is one possible week.
    //   waste pool  = HOU × waste/person            (what exists to be saved)
    //   protected   = BUN × engagement × lbs/container × household factor
    //   saved lbs   = pool × potential / (potential + pool)   (smooth cap)
    //   $ saved     = saved × price factor × (spend/HOU)/lbs-bought
    // Seeded per (answers, bundle): the same quiz always shows the same result.
    _simulate(bundleOverride) {
      const m = this._model;
      const HOU = Math.max(1, Math.min(10, this.state.people));
      const SPW = Math.max(1, this.state.spend);
      const BUN = bundleOverride || this.state.containers;
      const sel = (this.state.food && this.state.food.length ? this.state.food : ['produce']);
      let wSum = 0;
      sel.forEach((c) => { wSum += m.weights[c] || 0; });
      if (!(wSum > 0)) wSum = 1;

      const rnd = mulberry32(hash32(`v5|${HOU}|${SPW}|${sel.slice().sort().join('+')}|${BUN}|${this._cal}`));
      const z = () => {
        let u = 0;
        let v = 0;
        while (u === 0) u = rnd();
        while (v === 0) v = rnd();
        return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
      };
      const normalCI = ([lo, hi]) => (lo + hi) / 2 + ((hi - lo) / 3.29) * z();
      const logCI = ([lo, hi]) => Math.exp(Math.log(Math.sqrt(lo * hi)) + (Math.log(hi / lo) / 3.29) * z());

      const HSF = (1.8 * HOU) / (HOU + 1.6);
      const monthly = [];
      let lbsTotal = 0;
      for (let i = 0; i < this._draws; i++) {
        const wpp = Math.min(6, Math.max(2, normalCI(m.wpp)));
        const eng = Math.min(1, Math.max(0.05, logCI(m.eng)));
        const cpw = Math.max(0.05, logCI(m.cpw));
        const sto = BUN * eng * cpw * HSF;      // protected lbs
        const hwp = HOU * wpp;                  // waste pool lbs

        let r = rnd() * wSum;
        let cat = sel[sel.length - 1];
        for (const c of sel) { r -= m.weights[c] || 0; if (r <= 0) { cat = c; break; } }
        const wsc = Math.min(0.8, Math.max(0.05, normalCI(m.wsc[cat] || m.wsc.produce)));
        const cpf = Math.max(0.05, logCI(m.cpf[cat] || m.cpf.produce));

        const psl = sto * wsc;
        const sav = (hwp * psl) / (psl + hwp);  // capped saved lbs

        const pbp = Math.max(8, normalCI(m.pbp));
        const pri = (SPW / HOU) / pbp;

        monthly.push(sav * cpf * pri * 4.33 * this._cal);
        lbsTotal += sav * 4.33;
      }
      monthly.sort((a, b) => a - b);
      const N = monthly.length;
      const q = (p) => monthly[Math.min(N - 1, Math.floor(N * p))];
      const out = {
        mean: monthly.reduce((a, b) => a + b, 0) / N,
        median: q(0.5),
        q1: q(0.25),
        q3: q(0.75),
        p05: q(0.05),
        p95: q(0.95),
        lbsMonth: lbsTotal / N,
        saves: monthly,
      };
      // Kept for the submit payload — the full answer set ships with the lead.
      this._lastResult = out;
      return out;
    }

    _showResult({ median, p05, p95, lbsMonth, saves }) {
      this._show('result');
      this.progressEl.style.display = 'none';

      // Pulse the hero panel so bundle switches read as a live re-run.
      const hero = this.querySelector('[data-sw-hero]');
      if (hero) {
        hero.classList.remove('pulse');
        void hero.offsetWidth;
        hero.classList.add('pulse');
      }

      // Point the CTA at the bundle picked in step 1 (fallback: its own href).
      const cta = this.querySelector('[data-sw-cta]');
      const bundleLink = cta?.getAttribute(`data-link-${this.state.containers}`);
      if (cta && bundleLink) cta.href = bundleLink;
      this._toggleExplain(false); // always land on the chart, not the explainer
      // The median is the one canonical "typical" figure — headline, chart
      // marker, ledger and PDP strip all speak the same number.
      this._lastMean = Math.round(median);
      const set = (sel, text) => { const el = this.querySelector(sel); if (el) el.textContent = text; };
      set('[data-sw-yearly]', `$${Math.round(median * 12).toLocaleString()}`);
      set('[data-sw-lbs]', `${Math.round(lbsMonth)} lbs`);
      set('[data-sw-median]', `$${Math.round(median)}`);
      // Spec: show the honest 5–95% range next to the median.
      set('[data-sw-iqr]', `$${Math.round(p05)}–$${Math.round(p95)}`);
      set('[data-sw-pick-label]', this._bundleLabel(this._recommended));   // "12 + 4 free containers" (V11)

      this._renderDist(saves, p05, p95, median);

      // Count-up animation on the headline number.
      const el = this.querySelector('[data-sw-result]');
      const target = Math.round(median);
      const t0 = performance.now();
      const DUR = 1200;
      cancelAnimationFrame(this._countRaf);
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / DUR);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(target * eased);
        if (p < 1) this._countRaf = requestAnimationFrame(tick);
        else if (!this._celebrated) {
          this._celebrated = true;
          this._confetti();
        }
      };
      this._countRaf = requestAnimationFrame(tick);

      // Selection follows the current bundle (the label above the pills
      // names the recommendation, so no per-card badge).
      this.querySelectorAll('[data-sw-rebundle]').forEach((b) => {
        const on = Number(b.dataset.swRebundle) === this.state.containers;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      this._freshResult = false;

      // No-scroll guarantee: measure real overflow and let the chart absorb
      // exactly that many pixels (down to a sane floor).
      requestAnimationFrame(() => this._fitResult());
    }

    // "How we calculate": swaps the chart card's content for the in-card
    // methodology view (and back) — no navigation.
    _toggleExplain(open) {
      const main = this.querySelector('[data-sw-chart-main]');
      const panel = this.querySelector('[data-sw-explain]');
      if (!main || !panel) return;
      main.hidden = open;
      panel.hidden = !open;
      this.querySelector('[data-sw-explain-open]')?.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) panel.querySelector('.sw-chart-explain__back')?.focus({ preventScroll: true });
    }

    // Result-screen bundle switch: re-run the simulation for a different
    // container count without leaving the result view.
    _rebundle(containers) {
      if (containers === this.state.containers) return;
      this._beacon('wizard_rebundle', { containers });
      this.state.containers = containers;
      this._showResult(this._allStats?.[containers] || this._simulate(containers));
    }

    /* Monte-Carlo histogram: gradient bars (one hue), shaded p5-p95 band,
       clamped "avg" pill marker, edge-safe axis labels, per-bin tooltip. */
    _renderDist(saves, p05, p95, mean, forceH) {
      const host = this.querySelector('[data-sw-dist]');
      if (!host || !saves?.length) return;
      this._distData = { saves, p05, p95, mean };

      const W = Math.max(240, host.clientWidth || 320);
      // Chart height is viewport-proportional (no-scroll result screen):
      // ~20% of the viewport, clamped — and the fit pass can shrink it
      // further to absorb any remaining overflow exactly.
      const H = forceH || Math.round(Math.max(118, Math.min(250, (window.innerHeight || 800) * 0.2)));
      const TOP = 40;            // reserved for the typical pill
      const BASE = H - 32;
      const LABEL_Y = H - 10;
      const N = saves.length;
      const lo = Math.max(0, saves[Math.floor(N * 0.01)]);
      const hi = Math.max(lo + 1, saves[Math.min(N - 1, Math.floor(N * 0.99))]);
      const BINS = Math.min(26, Math.max(14, Math.round(W / 22)));
      const counts = new Array(BINS).fill(0);
      for (const v of saves) {
        const idx = Math.min(BINS - 1, Math.max(0, Math.floor(((v - lo) / (hi - lo)) * BINS)));
        counts[idx] += 1;
      }
      const maxC = Math.max(...counts);
      const gap = 2;
      const bw = (W - gap * (BINS - 1)) / BINS;
      const x = (v) => Math.max(0, Math.min(W, ((v - lo) / (hi - lo)) * W));

      let bars = '';
      this._binMeta = [];
      for (let i = 0; i < BINS; i++) {
        const h = maxC ? Math.max(counts[i] ? 3 : 0, (counts[i] / maxC) * (BASE - TOP - 4)) : 0;
        const bx = i * (bw + gap);
        const b0 = lo + ((hi - lo) * i) / BINS;
        const b1 = lo + ((hi - lo) * (i + 1)) / BINS;
        const mid = (b0 + b1) / 2;
        const inBand = mid >= p05 && mid <= p95;
        this._binMeta.push({ b0, b1, count: counts[i] });
        if (!h) continue;
        bars += `<rect data-bin='${i}' x='${bx.toFixed(1)}' y='${(BASE - h).toFixed(1)}' width='${bw.toFixed(1)}' height='${h.toFixed(1)}' rx='3' fill='${inBand ? "url(#sw-grad)" : "#c5e5ab"}' style='animation-delay:${(i * 18)}ms'/>`;
      }

      // Typical pill: clamped so it can never clip the viewBox edges.
      const label = `Typical · $${Math.round(mean)}/mo`;
      const pillW = label.length * 7.4 + 22;
      const lineX = x(mean);
      const pillX = Math.max(pillW / 2 + 2, Math.min(W - pillW / 2 - 2, lineX));

      host.innerHTML = `
        <svg viewBox='0 0 ${W} ${H}' role='img' aria-label='Distribution of ${N} simulated weeks of monthly savings, from $${Math.round(lo)} to $${Math.round(hi)}. Typical $${Math.round(mean)} per month.'>
          <defs>
            <linearGradient id='sw-grad' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0' stop-color='#2e9e6b'/>
              <stop offset='1' stop-color='#00623d'/>
            </linearGradient>
          </defs>
          ${bars}
          <line x1='0' y1='${BASE}' x2='${W}' y2='${BASE}' stroke='rgba(0,98,61,0.22)' stroke-width='1'/>
          <line x1='${lineX.toFixed(1)}' y1='${TOP - 3}' x2='${lineX.toFixed(1)}' y2='${BASE}' stroke='#1a1a1a' stroke-width='1.5' stroke-dasharray='3 3'/>
          <g>
            <rect x='${(pillX - pillW / 2).toFixed(1)}' y='0' width='${pillW.toFixed(1)}' height='26' rx='7' fill='#1a1a1a'/>
            <text x='${pillX.toFixed(1)}' y='17.5' text-anchor='middle' font-size='13' font-weight='700' fill='#ffffff'>Typical · <tspan fill='#9FD9B4'>$${Math.round(mean)}/mo</tspan></text>
          </g>
          <text x='2' y='${LABEL_Y}' font-size='11' font-weight='500' letter-spacing='0' fill='#6b6b6b'>$${Math.round(lo)}</text>
          <text x='${W / 2}' y='${LABEL_Y}' text-anchor='middle' font-size='11' font-weight='500' letter-spacing='0' fill='#9a9a9a'>monthly savings</text>
          <text x='${W - 2}' y='${LABEL_Y}' text-anchor='end' font-size='11' font-weight='500' letter-spacing='0' fill='#6b6b6b'>$${Math.round(hi)}</text>
        </svg>`;

      // Per-bin tooltip (pointer + touch).
      const tip = this.querySelector('[data-sw-dist-tip]');
      const svg = host.querySelector('svg');
      if (!tip || !svg) return;
      svg.addEventListener('pointermove', (e) => {
        const bar = e.target.closest('rect[data-bin]');
        if (!bar) { tip.hidden = true; return; }
        const m = this._binMeta[Number(bar.dataset.bin)];
        if (!m) { tip.hidden = true; return; }
        tip.textContent = `$${Math.round(m.b0)}–$${Math.round(m.b1)} · ${m.count} of ${N} weeks`;
        // Position against the tip's actual offset parent (the chart card),
        // not the svg host — otherwise the card padding skews the placement.
        const baseRect = (tip.offsetParent || host).getBoundingClientRect();
        const barRect = bar.getBoundingClientRect();
        const cx = Math.max(70, Math.min(baseRect.width - 70, barRect.left - baseRect.left + barRect.width / 2));
        tip.style.left = `${cx}px`;
        tip.style.top = `${Math.max(30, barRect.top - baseRect.top - 6)}px`;
        tip.hidden = false;
      });
      svg.addEventListener('pointerleave', () => { tip.hidden = true; });
    }

    // Shrinks the histogram until the result screen fits its card without
    // scrolling. Runs after layout; extra passes cover rounding.
    _fitResult(depth = 0) {
      const card = this.querySelector('.sw-card');
      const svg = this.querySelector('.sw-dist svg');
      const d = this._distData;
      if (!card || !svg || !d || depth > 2) return;
      const overflow = card.scrollHeight - card.clientHeight;
      if (overflow <= 0) return;
      const vb = svg.viewBox.baseVal;
      const scale = (svg.getBoundingClientRect().height || vb.height) / vb.height;
      const H2 = Math.max(76, Math.round(vb.height - overflow / scale - 2));
      if (H2 >= vb.height) return;
      this._renderDist(d.saves, d.p05, d.p95, d.mean, H2);
      requestAnimationFrame(() => this._fitResult(depth + 1));
    }

    _confetti() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const host = this.querySelector('[data-sw-confetti]');
      if (!host) return;
      host.innerHTML = '';
      for (let i = 0; i < 32; i++) {
        const piece = document.createElement('i');
        piece.style.setProperty('--clr', CONFETTI_COLORS[i % CONFETTI_COLORS.length]);
        piece.style.setProperty('--dx', `${(Math.random() - 0.5) * 460}px`);
        piece.style.setProperty('--dy', `${180 + Math.random() * 260}px`);
        piece.style.setProperty('--rot', `${(Math.random() - 0.5) * 900}deg`);
        piece.style.setProperty('--delay', `${Math.random() * 0.25}s`);
        host.appendChild(piece);
      }
      clearTimeout(this._confettiTimer);
      this._confettiTimer = setTimeout(() => { host.innerHTML = ''; }, 2200);
    }
  }

  customElements.define('savings-wizard', SavingsWizard);
})();

